import React from 'react';

interface NewsletterSignupProps {
  className?: string;
}

export function NewsletterSignup({ className = '' }: NewsletterSignupProps) {
  const [emailTouched, setEmailTouched] = React.useState(false);
  const [isEmailValid, setIsEmailValid] = React.useState(true);
  // Dodajemy stan do przechowywania wartości maila, żeby móc go sprawdzić przy wysyłce
  const [emailValue, setEmailValue] = React.useState('');

  // WALIDACJA EMAILA (REGEX)
  // Wymaga:
  // - poprawnej części lokalnej (litery/cyfry i ._%+-)
  // - @
  // - nazwy domeny (litery/cyfry i .-)
  // - kropki
  // - końcówki TLD min. 2 znaki (np. .pl, .com)
  const validateEmail = (email: string) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = event.target.value;
    setEmailValue(newVal);
    
    // Waliduj od razu, jeśli użytkownik już raz dotknął pola
    if (emailTouched) {
      setIsEmailValid(validateEmail(newVal));
    }
  };

  // Zatrzymujemy wysyłkę, jeśli walidacja nie przechodzi
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const isValid = validateEmail(emailValue);

    if (!isValid) {
      event.preventDefault(); // STOP! Nie wysyłaj do Mailchimp – pierwsza instrukcja w ścieżce błędu
      setEmailTouched(true);
      setIsEmailValid(false);
      return;
    }
    // Jeśli isValid === true, przeglądarka wyśle formularz normalnie do Mailchimp
  };

  const emailInputClasses = [
    'form-input newsletter-input w-full px-4 py-3 rounded-lg',
    'bg-transparent appearance-none',
    'border',
    'focus:outline-none focus:ring-2', 
    !isEmailValid && emailTouched
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-white/50 focus:border-accessible-red-600 focus:ring-accessible-red-600',
  ].join(' ');

  return (
    <section
      id="mc_embed_shell"
      className={className}
      aria-labelledby="newsletter-footer-title"
    >
      <style>{`
        #mce-EMAIL {
          color: #ffffff !important;
          caret-color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        #mce-EMAIL:-webkit-autofill,
        #mce-EMAIL:-webkit-autofill:hover, 
        #mce-EMAIL:-webkit-autofill:focus, 
        #mce-EMAIL:-webkit-autofill:active {
            -webkit-text-fill-color: #ffffff !important;
            transition: background-color 5000s ease-in-out 0s;
        }
        #mce-EMAIL::placeholder {
          color: rgba(255, 255, 255, 0.7) !important;
          opacity: 1;
        }
        label[for="mce-EMAIL"] {
          color: #ffffff !important;
        }
      `}</style>

      <div
        id="mc_embed_signup"
        className="bg-accessible-gray-900/70 dark:bg-accessible-gray-900/80 high-contrast:bg-black rounded-2xl p-6 md:p-8"
      >
        <div
          id="mc_embed_signup_scroll"
          className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
        >
          <div className="max-w-md">
            <h2 id="newsletter-footer-title" className="heading-6-white mb-2">
              Bądź z nami na bieżąco
            </h2>
            <p className="body-small-white-80 no-margin">
              Zapisz się do newslettera, aby otrzymywać informacje o nowych
              podopiecznych, akcjach specjalnych i możliwościach wsparcia
              fundacji.
            </p>
          </div>

          <form
            action="https://znajdki.us8.list-manage.com/subscribe/post?u=cb2fb8c2a6147992e52a25a76&amp;id=9df32f4369&amp;f_id=00192fe3f0"
            method="post"
            id="mc-embedded-subscribe-form"
            name="mc-embedded-subscribe-form"
            target="_self"
            onSubmit={handleSubmit} /* PODPIĘCIE WALIDACJI PRZED WYSYŁKĄ */
            className="w-full md:max-w-md space-y-3 validate"
            noValidate /* Wyłączamy domyślną walidację przeglądarki, bo mamy własną, lepszą */
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <label
                  htmlFor="mce-EMAIL"
                  className="form-label !text-white mb-1 block"
                  style={{ color: '#ffffff' }}
                >
                  Adres e-mail
                  <span className="ml-1 !text-red-400" style={{ color: '#fc8181' }}>*</span>
                </label>
                
                <input
                  type="email"
                  id="mce-EMAIL"
                  name="EMAIL"
                  // Usunięto 'required', bo obsłużymy to ręcznie w handleSubmit
                  className={`${emailInputClasses} required email`}
                  placeholder="Twój adres e-mail"
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={!isEmailValid && emailTouched}
                  value={emailValue} // Kontrolowane wejście
                  onBlur={handleEmailBlur}
                  onChange={handleEmailChange}
                />
                
                {/* {!isEmailValid && emailTouched && (
                  <p className="form-error absolute top-full left-0 mt-1 text-red-400 text-sm whitespace-nowrap z-10">
                    Podaj poprawny adres e-mail.
                  </p>
                )} */}
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  id="mc-embedded-subscribe"
                  name="subscribe"
                  className="btn-text-medium bg-accessible-red-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-accessible-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accessible-red-600 focus-visible:ring-offset-2 focus-visible:ring-offset-accessible-gray-900 transition-colors w-full sm:w-auto whitespace-nowrap"
                  aria-label="Zapisz się do newslettera Fundacji Znajdki"
                >
                  Zapisz się
                </button>
              </div>
            </div>

            <input type="hidden" name="tags" value="5430435" />

            <div className="sr-only" aria-hidden="true">
              <label htmlFor="mc-b-cb2fb8c2a6147992e52a25a76_9df32f4369">
                Pozostaw to pole puste
              </label>
              <input
                id="mc-b-cb2fb8c2a6147992e52a25a76_9df32f4369"
                type="text"
                name="b_cb2fb8c2a6147992e52a25a76_9df32f4369"
                tabIndex={-1}
                defaultValue=""
              />
            </div>

            <p className="form-helper text-white/70 mt-2 text-sm">
              Zapisując się, akceptujesz naszą{' '}
              <a
                href="/polityka-prywatnosci"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Politykę Prywatności
              </a>{' '}
              i{' '}
              <a
                href="/regulamin"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Regulamin
              </a>
              {' '}oraz wyrażasz zgodę na otrzymywanie informacji handlowych. 
              Newsletter obsługiwany jest przez platformę Mailchimp. 
              W każdej chwili możesz zrezygnować z subskrypcji, klikając link w wiadomości.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default NewsletterSignup;